import {ColumnAggregationType} from "utils/enums";
import { timeSpanToSeconds } from "utils/time-utils";
import {HighlightColor, HighlightRange, Nullable, IColumnsWithValuesProps} from "./interfaces";

const isInRange = (value: number | null | undefined, range: HighlightRange) => {
    if (value == null) return false;

    const rMin = range.min?.value ?? Number.MIN_SAFE_INTEGER;
    const rMax = range.max?.value ?? Number.MAX_SAFE_INTEGER
    const minInclusive = range.min?.inclusive ?? false
    const maxInclusive = range.max?.inclusive ?? false


    if (rMin > value || (!minInclusive && rMin === value)) return false

    return !(rMax < value || (!maxInclusive && rMax === value));
}

const getValueColors = (value: number|undefined, {
    defaultHighlightColor,
    highlightRanges
}: { highlightRanges?: Nullable<Array<HighlightRange>>, defaultHighlightColor?: Nullable<HighlightColor> }) => {
    let bgColor;
    let color;
    let newValue = value;
    
    if (typeof value === "object") {
      const seconds = timeSpanToSeconds(value);
      newValue = seconds === 0 ? undefined : seconds;
    }
    
    if (highlightRanges && typeof newValue === "number") {
      
      highlightRanges.forEach(range => {
        if (isInRange(newValue, range)) {
          range.color && (color = range.color);
          range.bgColor && (bgColor = range.bgColor);
        }
      });
    }

    if (defaultHighlightColor && !bgColor && !color) {
        defaultHighlightColor.color && (color = defaultHighlightColor.color);
        defaultHighlightColor.bgColor && (bgColor = defaultHighlightColor.bgColor);
    }

    return [color, bgColor];
}

const getHighlightColor = (highlightColor?: Nullable<HighlightColor>) => {
    const bgColor = highlightColor?.bgColor;
    const color = highlightColor?.color;

    return [color, bgColor];
}

const checkForUnwantedType = (type: string | undefined) => {
    return !(type === "USER_SIGNATURE" || type === "PARAMETERS" || type === "FOREIGN_KEY")
}

const checkForHighlightType = (type: string | undefined) => {
    return type === "DOUBLE" || type === "LONG" || type === "TIMESPAN"
}

const getDataStringParamsToObject = (dateFrom?: string, dateTo?: string, filter?: string) => {
    if (dateFrom || dateTo || filter) {
        const tempSearchParams: { dateFrom?: string, dateTo?: string, filter?: string } = {};
        if (dateFrom) {
            tempSearchParams.dateFrom = dateFrom;
        }
        if (dateTo) {
            tempSearchParams.dateTo = dateTo;
        }
        if (filter) {
            tempSearchParams.filter = filter;
        }
        return {params: tempSearchParams}
    }
    return undefined
}

const filterPreviewValidation = (dateFrom?: string, dateTo?: string, filter?: string): { params: any } | undefined => {
    if (!dateFrom && !dateTo && !filter) {
        return undefined
    }
    if(dateFrom && !dateTo && !filter){
        return undefined
    }
    if(!dateFrom && dateTo && !filter){
        return undefined
    }

    const tempSearchParams: { dateFrom?: string, dateTo?: string, filter?: string } = {};
    if (dateFrom) {
        tempSearchParams.dateFrom = dateFrom;
    }
    if (dateTo) {
        tempSearchParams.dateTo = dateTo;
    }
    if (filter) {
        tempSearchParams.filter = filter;
    }

    return {params: tempSearchParams}
}

const arrayToCsv = (arr: Array<any>) => {
    if (!arr) {
        return '';
    }
    let csv = `${arr[0].map((item: any) => item.name).join(',')  }\n`;
    for (let i = 0; i < arr.length; i++) {
        csv += `${arr[i].map((item: any) => `"${item.value}"`).join(',')  }\n`;
    }
    return csv;
}

const downloadBlob = (content: any, filename: string, contentType: string) => {
    const blob = new Blob([content], {type: contentType});
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

const s2ab = (s: any) => {
    const buf = new ArrayBuffer(s.length);
    const view = new Uint8Array(buf);

    for (let i = 0; i < s.length; i++) {
        view[i] = s.charCodeAt(i) & 0xff;
    }

    return buf;
}

const columnsTotalsAggregation = (columns: IColumnsWithValuesProps[]) => {
    return columns.map((col) => {
            if (col.aggregationType === ColumnAggregationType.SUM) {
                col.total = col.values.reduce((a: number, b: number) => a + b, 0);
            }

            if (col.aggregationType === ColumnAggregationType.MEAN) {
                col.total = (col.values.reduce((a: number, b: number) => a + b, 0) / col.values.length);
            }

            if (col.aggregationType === ColumnAggregationType.COUNT) {
                col.total = col.values.length;
            }
            return col;
        }
    );
};

const handlePrecision = (number: number, precision: number, output: boolean = false) => {
    try {
        if (output) {
            return new Intl.NumberFormat('lt-LT', {
                minimumFractionDigits: precision,
                maximumFractionDigits: precision
            })
                .format(number)
        } 
            return number.toFixed(precision);
        
    } catch (err) {
        return number;
    }
}

const formatDataPatternToCalendar = (datePattern: string) => {
  return datePattern && typeof datePattern === "string"
    ? datePattern.replace(/yyyy/g, "yy").replace(/MM/g, "mm")
    : "";
};

export {
  isInRange,
  getValueColors,
  getHighlightColor,
  checkForUnwantedType,
  checkForHighlightType,
  getDataStringParamsToObject,
  filterPreviewValidation,
  arrayToCsv,
  downloadBlob,
  s2ab,
  columnsTotalsAggregation,
  handlePrecision,
  formatDataPatternToCalendar,
};